import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { Modal, Tab, Tabs, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import AuthContext from '../../../contexts/authContext';
import DetailsTab from './interventionDetailsComponents/DetailsTab';
import ChronologieTab from './interventionDetailsComponents/ChronologieTab';
import RapportModal from './RapportModal';
import EditFormModal from './interventionDetailsComponents/EditFormModal';
import showNotification from '../../../components/extras/showNotification';
import PlanifierPoseModal from './PlanifierPoseModal';
import { useNavigate } from 'react-router-dom';
import DupliquerIntervention from './DupliquerIntervention';
import PaiementsTab from './interventionDetailsComponents/PaiementsTab';
import DevisTab from './interventionDetailsComponents/DevisTab';
import RapportTab from './interventionDetailsComponents/RapportTab';
import moment from 'moment';
import { Protected } from 'layout/Protected';
import AccessContext from 'contexts/accessContext';
import MaterialsTab from './interventionDetailsComponents/MaterialsTab';
import { getAPI, postAPI, putAPI } from 'apis/backend/apiNode';
import EmailSendPopup from 'pages/comptabilite/CommunComptabilite/EmailSendPopup';
import { addChronologieEntry } from '../hooks/planningService';

const InterventionDetailsModal = ({
    show,
    onHide,
    intervention,
    cars = [],
    onUpdateIntervention,
    onDeleteIntervention,
    CHRONOLOGIE_TYPES = {},
    INTERVENTION_TYPES = [],
    INTERVENTION_MOTIFS = [],
    onAddChronologieEntry,
    getChronologieByIntervention,
    getInterventionById,
    setSelectedIntervention,
    reloadData,
}) => {
    const { userData } = useContext(AuthContext);
    const { access } = useContext(AccessContext);

    const navigate = useNavigate();

    const isTechnician = userData?.function?.name?.includes('Technicien')
    const interventionId = intervention?._id;

    const [activeTab, setActiveTab] = useState('details');
    const [loading, setLoading] = useState(false);
    const [showRapportModal, setShowRapportModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingRapport, setEditingRapport] = useState(null);
    const [refreshRapportTrigger, setRefreshRapportTrigger] = useState(0);
    const [savingChanges, setSavingChanges] = useState(false);
    const [showPlanifierPoseModal, setShowPlanifierPoseModal] = useState(false);
    const [showDupliquerInterventionModal, setShowDupliquerInterventionModal] = useState(false);
    const [showPoseConfirm, setShowPoseConfirm] = useState(false);

    const [openEmail, setOpenEmail] = useState(false);
    const [emailType, setEmailType] = useState('Rappel');



    const handleEmailSent = async (emailDataPayload) => {
        try {
            await addChronologieEntry(
                interventionId,
                { type: 'email', note: 'Email du sujet : ' + emailDataPayload.subject + " est envoyé à : " + emailDataPayload.to, createdBy: userData?.id },
                userData?.id
            );
            const chrono = await getChronologieByIntervention(interventionId);

            setSelectedIntervention({
                ...intervention,
                chronologie: chrono,
            });
        } catch (err) {
            console.warn('Could not add chronologie entry for cancellation:', err);
        }
        showNotification('Succès', 'Email envoyé avec succès', 'success');
    };

    useEffect(() => {
        if (show && interventionId) {
            (async () => {
                const chrono = await getChronologieByIntervention(interventionId);

                setSelectedIntervention({
                    ...intervention,
                    chronologie: chrono,
                });
            })();
        }
    }, [show, interventionId, getChronologieByIntervention, setSelectedIntervention]);

    const getUserDisplayName = useCallback((user) => {
        if (!user) return 'Système';
        if (typeof user === 'string') return 'Utilisateur';
        return (
            user.name ||
            user.userName ||
            `${user.nom || ''} ${user.prenom || ''}`.trim() ||
            'Utilisateur inconnu'
        );
    }, []);

    const formatDateTime = useCallback((date) => {
        if (!date) return 'Date inconnue';
        return moment.utc(date).format('DD/MM/YYYY HH:mm')
    }, []);

    const getTypeIcon = useCallback((type) => {
        const icons = {
            intervention: '🔧 Intervention',
            devis: '📄 Devis',
            reclamation: '⚠️ Réclamation',
            reclamation_garantie: '⚠️ Réclamation',
            deblocage: '🔓 Déblocage',
            prendre_mesure: '📏 Prise de mesure',
            enlevement: '🚚 Enlèvement',
        };
        return icons[type] || '📋 ' + type;
    }, []);

    const getTypeColor = useCallback(() => 'primary', []);

    const handleOpenEditModal = useCallback(() => {
        setShowEditModal(true);
    }, []);

    const handleSaveChanges = useCallback(
        async (updateData) => {
            if (!interventionId) return;

            setSavingChanges(true);
            try {
                const userId = userData?._id || userData?.id;
                if (!userId) throw new Error('Utilisateur non identifié');

                const fullUpdateData = {
                    ...updateData,
                    updatedBy: userId,
                    performedBy: userId,
                };

                const updatedIntervention = await onUpdateIntervention(
                    interventionId,
                    fullUpdateData,
                );

                if (updatedIntervention) {
                    // setSelectedIntervention((prev) => ({
                    //     ...prev,
                    //     ...updatedIntervention,
                    // }));

                    try {
                        const chronologie = await getChronologieByIntervention(interventionId);
                        // setSelectedIntervention((prev) => ({
                        //     ...prev,
                        //     chronologie,
                        // }));
                    } catch (error) {
                        console.warn('Could not refresh chronologie data:', error);
                    }
                    const freshIntervention = await getInterventionById(interventionId);

                    // Update the selected intervention with complete fresh data
                    setSelectedIntervention(freshIntervention);
                    reloadData();

                }

                setShowEditModal(false);
            } catch (error) {
                console.error('Erreur lors de la sauvegarde:', error);
                showNotification('Erreur', 'Erreur lors de la sauvegarde', 'danger');
            } finally {
                setSavingChanges(false);
            }
        },
        [
            interventionId,
            userData,
            onUpdateIntervention,
            getChronologieByIntervention,
            setSelectedIntervention,
        ],
    );

    const handleDelete = useCallback(() => {
        if (!interventionId) return;
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette intervention ?')) {
            onDeleteIntervention(interventionId);
            onHide();
        }
    }, [interventionId, onDeleteIntervention, onHide]);

    const handleCompleteIntervention = useCallback(async () => {
        if (!interventionId) return;

        try {
            const [interventionData, chronologie] = await Promise.all([
                //  getInterventionById(interventionId),
                getChronologieByIntervention(interventionId),
            ]);

            // Check if rapport_intervention exists and has description
            if (!intervention?.rapport_intervention?.description_travaux) {
                showNotification(
                    'Impossible de terminer',
                    'Créez un rapport avec description des travaux avant de terminer',
                    'danger',
                );
                return;
            }

            setSelectedIntervention({
                ...intervention,
                chronologie,
            });

            if (!window.confirm('Terminer cette intervention ?')) return;

            const userId = userData?._id || userData?.id;
            await onUpdateIntervention(interventionId, {
                statut: 'termine',
                updatedBy: userId,
                performedBy: userId,
            });

            onHide();
            showNotification('Succès', 'Intervention terminée', 'success');
        } catch (error) {
            console.error('Erreur lors de la complétion:', error);
            showNotification('Erreur', 'Échec de la complétion de l\'intervention', 'danger');
        }
    }, [interventionId, userData, onUpdateIntervention, onHide, getInterventionById, getChronologieByIntervention, setSelectedIntervention]);

    const handleCreateRapport = useCallback(() => {
        setEditingRapport(null);
        setShowRapportModal(true);
    }, []);

    const handleEditRapport = useCallback((rapportData) => {
        setEditingRapport(rapportData);
        setShowRapportModal(true);
    }, []);



    const handleSaveRapport = useCallback(
        async (rapportData) => {
            try {
                if (!interventionId) {
                    throw new Error('ID d\'intervention manquant');
                }

                const userId = userData?._id || userData?.id;

                // Fix: Properly format paiements array
                let formattedPaiements = [];
                if (rapportData.paiements && Array.isArray(rapportData.paiements)) {
                    formattedPaiements = rapportData.paiements.map(paiement => ({
                        montant: parseFloat(paiement.montant) || 0,
                        methode_paiement: paiement.methode_paiement || 'espèces',
                        destination_journal_id: paiement.destination_journal_id || '',
                        date_paiement: paiement.date_paiement || paiement.date_payment || new Date().toISOString(),
                        reference: intervention.reference || '',
                        statut: paiement.statut || 'en_attente',
                        notes: paiement.notes || '',
                        payment_id: paiement.payment_id || paiement.python_payment_id || '',
                        currency: paiement.currency || 'EUR',
                        payment_type: paiement.payment_type || 'inbound'
                    }));
                }

                const updatedIntervention = await onUpdateIntervention(interventionId, {
                    rapport_intervention: {
                        description_travaux: rapportData.description_travaux || '',
                        devis: rapportData.devis || [],
                        total_paiements: rapportData.total_paiements || 0,
                        total_paiements_valides: rapportData.total_paiements_valides || 0,
                        voiceRecordings: rapportData.voiceRecordings || [],
                        attachments: rapportData.attachments || [],
                        paiements: formattedPaiements,
                        created_at: rapportData.created_at || new Date().toISOString(),
                        updatedBy: userId,
                        updatedAt: new Date().toISOString(),
                        pose_faite: rapportData.pose_faite,
                        pose_sur_place: rapportData.pose_sur_place
                    },
                    updatedBy: userId,
                    performedBy: userId,
                });

                if (updatedIntervention) {
                    // Force refresh by getting the latest data
                    const freshIntervention = await getInterventionById(interventionId);

                    // Update the selected intervention with complete fresh data
                    setSelectedIntervention(freshIntervention);

                    // Trigger reload of parent data
                    reloadData();

                    setRefreshRapportTrigger((prev) => prev + 1);
                    setShowRapportModal(false);

                    showNotification(
                        'Succès',
                        editingRapport ? 'Rapport mis à jour' : 'Rapport créé',
                        'success'
                    );

                    return updatedIntervention.rapport_intervention;
                }
            } catch (error) {
                console.error('❌ Erreur sauvegarde rapport:', error);
                showNotification(
                    'Erreur',
                    error.message || 'Erreur lors de la sauvegarde du rapport',
                    'danger',
                );
                throw error;
            }
        },
        [interventionId, userData, onUpdateIntervention, editingRapport, setSelectedIntervention, getInterventionById, reloadData],
    );


    const handleDeleteRapport = useCallback(
        async () => {
            if (!interventionId) return;

            if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
                try {
                    const userId = userData?._id || userData?.id;

                    const updatedIntervention = await onUpdateIntervention(interventionId, {
                        rapport_intervention: null,
                        updatedBy: userId,
                        performedBy: userId,
                    });

                    if (updatedIntervention) {
                        setSelectedIntervention((prev) => ({
                            ...prev,
                            rapport_intervention: null,
                        }));

                        setRefreshRapportTrigger((prev) => prev + 1);
                        setShowRapportModal(false);

                        showNotification('Succès', 'Rapport supprimé', 'success');
                    }
                } catch (error) {
                    console.error('❌ Erreur suppression rapport:', error);
                    showNotification('Erreur', 'Erreur lors de la suppression du rapport', 'danger');
                }
            }
        },
        [interventionId, userData, onUpdateIntervention, setSelectedIntervention],
    );

    const detailsTabProps = useMemo(
        () => ({
            intervention,
            cars,
            INTERVENTION_TYPES,
            INTERVENTION_MOTIFS,
            formatDateTime,
            getTypeColor,
            getTypeIcon,
            getUserDisplayName,
            onOpenEditModal: handleOpenEditModal,
            reloadData,
            setSelectedIntervention
        }),
        [
            intervention,
            cars,
            INTERVENTION_TYPES,
            INTERVENTION_MOTIFS,
            formatDateTime,
            getTypeColor,
            getTypeIcon,
            getUserDisplayName,
            handleOpenEditModal,
            reloadData,
            setSelectedIntervention
        ],
    );

    const chronologieTabProps = useMemo(
        () => ({
            intervention,
            CHRONOLOGIE_TYPES,
            onAddChronologieEntry,
            getChronologieByIntervention,
            userData,
            onHide,
            getUserDisplayName,
            navigate,
        }),
        [
            intervention,
            CHRONOLOGIE_TYPES,
            onAddChronologieEntry,
            getChronologieByIntervention,
            userData,
            onHide,
            getUserDisplayName,
            navigate,
        ],
    );

    const rapportTabProps = useMemo(
        () => ({
            intervention,
            onUpdateRapport: handleEditRapport,
            setSelectedIntervention
        }),
        [
            intervention,
            setSelectedIntervention,
            handleEditRapport,

        ],
    );


    // const rapportTabProps = useMemo(
    //     () => ({
    //         intervention,
    //         interventionId,
    //         onCreateRapport: handleCreateRapport,
    //         onUpdateRapport: handleEditRapport,
    //         onDeleteRapport: handleDeleteRapport,
    //         formatDateTime,
    //         getUserDisplayName,
    //         refreshTrigger: refreshRapportTrigger,
    //         onDevisUpdated: reloadData,
    //     }),
    //     [
    //         intervention,
    //         handleCreateRapport,
    //         handleEditRapport,
    //         handleDeleteRapport,
    //         formatDateTime,
    //         getUserDisplayName,
    //         refreshRapportTrigger,
    //         reloadData,
    //     ],
    // );


    const paiementTabProps = useMemo(
        () => ({
            intervention,
            setIntervention: setSelectedIntervention,
        }),
        [
            intervention,
        ],
    );

    const materialsTabProps = useMemo(
        () => ({
            intervention, // or just intervention
            setIntervention: setSelectedIntervention,

        }),
        [intervention]
    );

    const devisTabProps = useMemo(
        () => ({
            intervention,
            setIntervention: setSelectedIntervention,
        }),
        [
            intervention,
        ],
    );


    const getStatusVariant = (s) => {
        if (!s) return 'secondary';
        return (
            { planifie: 'info', en_cours: 'warning', termine: 'success', annule: 'danger' }[s] ||
            'secondary'
        );
    };

    const getStatus = (s) => {
        return (
            { a_planifie: 'A Planifier', planifie: 'Planifié', en_cours: 'En cours', termine: 'terminé', annule: 'Annulé' }[s]
        );
    };

    if (!show) return null;

    if (!intervention) {
        return (
            <Modal show={show} onHide={onHide} size='lg' centered>
                <Modal.Header closeButton className='border-0'>
                    <Modal.Title className='text-danger'>Erreur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant='danger' className='mb-0'>
                        Aucune donnée d'intervention disponible
                    </Alert>
                </Modal.Body>
            </Modal>
        );
    }

    if (loading) {
        return (
            <Modal show={show} onHide={onHide} size='lg' centered>
                <Modal.Header closeButton className='border-0'>
                    <Modal.Title className='text-primary'>Chargement...</Modal.Title>
                </Modal.Header>
                <Modal.Body className='text-center py-5'>
                    <Spinner animation='border' variant='primary' className='mb-3' />
                    <p className='text-muted'>Chargement des détails...</p>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <>
            {interventionId && (
                <EditFormModal
                    show={showEditModal}
                    onHide={() => setShowEditModal(false)}
                    intervention={intervention}
                    cars={cars}
                    onSave={handleSaveChanges}
                    loading={savingChanges}
                    INTERVENTION_TYPES={INTERVENTION_TYPES}
                    INTERVENTION_MOTIFS={INTERVENTION_MOTIFS}
                    reloadData={reloadData}
                />
            )}

            <RapportModal
                show={showRapportModal}
                onHide={() => setShowRapportModal(false)}
                intervention={intervention}
                rapport={editingRapport || intervention?.rapport_intervention}
                onSave={handleSaveRapport}
                onDelete={handleDeleteRapport}
                isEditing={!!editingRapport}
                reloadData={reloadData}
            />
            {!!showPlanifierPoseModal &&

                <PlanifierPoseModal
                    show={showPlanifierPoseModal}
                    onHide={() => setShowPlanifierPoseModal(false)}
                    intervention={intervention}
                    cars={cars}
                    onPoseCreated={(newPose) => {
                        showNotification(
                            'Succès',
                            `Pose ${newPose.reference} créée avec succès`,
                            'success',
                        );
                    }}
                    reloadData={reloadData}
                />
            }

            {!!showDupliquerInterventionModal &&

                <DupliquerIntervention
                    show={showDupliquerInterventionModal}
                    onHide={() => setShowDupliquerInterventionModal(false)}
                    intervention={intervention}
                    cars={cars}
                    onPoseCreated={(newPose) => {
                        showNotification(
                            'Succès',
                            `Intervention ${newPose.reference} créée avec succès`,
                            'success',
                        );
                    }}
                    reloadData={reloadData}
                />
            }
            <Modal
                show={show}
                onHide={onHide}
                size='xl'
                centered
                scrollable
                contentClassName='rounded-4 shadow-lg h-100'>
                {/* <Modal.Header className='bg-white border-bottom pt-3 pt-md-4 pb-2 pb-md-3' closeButton>
                    <div className='d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center w-100 gap-3 gap-sm-2'>
                        <div className='d-flex flex-column flex-sm-row align-items-start align-items-sm-center flex-grow-1 gap-2 gap-sm-3'>
                            <div className='d-flex align-items-center'>
                                <i className='fas fa-clipboard-list text-primary me-2 fs-5 fs-sm-4'></i>
                                <h5 className='fw-bold mb-0 text-dark fs-6 fs-sm-5'>
                                    {intervention?.reference || 'Intervention'}
                                </h5>
                            </div>
                            <div
                                className={`text-center px-3 px-sm-4 py-1 py-sm-2 rounded-pill bg-${getStatusVariant(
                                    intervention?.statut,
                                )} bg-opacity-10 text-${getStatusVariant(
                                    intervention?.statut,
                                )} fw-bold border border-${getStatusVariant(intervention?.statut)} fs-7 fs-sm-6`}>
                                {intervention?.statut?.toUpperCase() || 'INCONNU'}
                            </div>
                        </div>

                        <div className='d-flex flex-wrap gap-2 align-items-start w-100 w-sm-auto'>
                            {isTechnician ? (
                                intervention?.statut === 'en_cours' && (
                                    <Button
                                        variant='success'
                                        onClick={handleCompleteIntervention}
                                        size='sm'
                                        className='flex-grow-1 flex-sm-grow-0 px-2 px-sm-3'>
                                        <span className='d-none d-sm-inline'>Terminer</span>
                                        <span className='d-inline d-sm-none'>OK</span>
                                    </Button>
                                )
                            ) : (
                                <>
                                    {!isTechnician &&


                                        <Button
                                            variant='success'
                                            onClick={() => setShowPlanifierPoseModal(true)}
                                            size='sm'
                                            className='flex-grow-1 flex-sm-grow-0 px-2 px-sm-3'>
                                            <i className='fas fa-calendar-plus me-1 d-none d-sm-inline'></i>
                                            <span className='d-none d-sm-inline'>Planifier Pose</span>
                                            <span className='d-inline d-sm-none'>Pose</span>
                                        </Button>
                                    }
                                    <Button
                                        variant='success'
                                        onClick={() => setShowDupliquerInterventionModal(true)}
                                        size='sm'
                                        className='flex-grow-1 flex-sm-grow-0 px-2 px-sm-3'>
                                        <i className='fas fa-calendar-plus me-1 d-none d-sm-inline'></i>
                                        <span className='d-none d-sm-inline'>Nouvelle Intervention</span>
                                        <span className='d-inline d-sm-none'>Dupliquer</span>
                                    </Button>
                                    <Button
                                        variant='outline-danger'
                                        onClick={handleDelete}
                                        size='sm'
                                        className='flex-grow-1 flex-sm-grow-0 px-2 px-sm-3'>
                                        <i className='fas fa-trash me-1 d-none d-sm-inline'></i>
                                        <span className='d-none d-sm-inline'>Supprimer</span>
                                        <span className='d-inline d-sm-none'>Suppr.</span>
                                    </Button>
                                    <Button
                                        variant='primary'
                                        onClick={handleOpenEditModal}
                                        size='sm'
                                        className='flex-grow-1 flex-sm-grow-0 px-2 px-sm-3'>
                                        <i className='fas fa-edit me-1 d-none d-sm-inline'></i>
                                        <span className='d-none d-sm-inline'>Modifier</span>
                                        <span className='d-inline d-sm-none'>Edit</span>
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </Modal.Header> */}

                <Modal.Header className='bg-white border-bottom pt-3 pt-md-4 pb-2 pb-md-3' closeButton>
                    <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center w-100 gap-3'>

                        {/* --- LEFT: Reference & Status (Exactly as requested) --- */}
                        <div className='flex-shrink-0'>
                            <h5 className='fw-bold mb-0 text-dark fs-6 fs-sm-5'>
                                {intervention?.reference} - {getStatus(intervention?.statut)}
                            </h5>
                        </div>

                        {/* --- RIGHT: Split Action Buttons --- */}
                        <div className='d-flex flex-wrap gap-2 align-items-center justify-content-md-end flex-grow-1'>
                            {isTechnician ? (
                                intervention?.statut === 'en_cours' && (
                                    <Button variant='success' onClick={handleCompleteIntervention} size='sm' className='px-3'>
                                        Terminer
                                    </Button>
                                )
                            ) : (
                                <>
                                    {/* GROUP 1: Creation/Planning (Left side of the button group) */}
                                    <div className="d-flex gap-2">
                                        {intervention?.statut === "planifie" && (
                                            <Button variant='success'
                                                onClick={() => { setEmailType('Rappel'); setOpenEmail(true); }}

                                                size='sm'>
                                                Rappel
                                            </Button>
                                        )}
                                        {intervention?.statut === "planifie" && (
                                            <Button variant='success'
                                                onClick={() => { setEmailType('ConfirmerRDV'); setOpenEmail(true); }}

                                                size='sm'>
                                                Confirmer RDV
                                            </Button>
                                        )}
                                        {(
                                            intervention?.companyId?._id !== "69285f4d2b0b517051a84069" ||
                                            access?.includes?.("intervention:can_update") ||
                                            userData?.role === "sup_admin"
                                        ) &&
                                            (intervention?.statut === "a_planifie" ||
                                                intervention?.statut === "planifie" ||
                                                intervention?.statut === "en_cours") && (
                                                <Button
                                                    variant="primary"
                                                    onClick={handleOpenEditModal}
                                                    size="sm"
                                                    className="flex-grow-1 flex-sm-grow-0 px-2 px-sm-3"
                                                >
                                                    <i className="fas fa-edit me-1 d-none d-sm-inline"></i>
                                                    <span className="d-none d-sm-inline">Modifier</span>
                                                    <span className="d-inline d-sm-none">Edit</span>
                                                </Button>
                                            )}

                                        {intervention?.type === "devis" && intervention?.statut === "termine" && (
                                            <Button variant='success' onClick={() => setShowPlanifierPoseModal(true)} size='sm'>
                                                Planifier Pose
                                            </Button>
                                        )}
                                        {intervention?.statut !== "a_planifie" && <Button variant='success' onClick={() => setShowDupliquerInterventionModal(true)} size='sm'>
                                            Nouvelle Intervention
                                        </Button>}

                                        {(intervention?.statut == "planifie" || intervention?.statut == "a_planifie") &&
                                            <Protected action='intervention:delete'>
                                                <Button
                                                    variant='outline-danger'
                                                    onClick={handleDelete}
                                                    size='sm'
                                                    className='flex-grow-1 flex-sm-grow-0 px-2 px-sm-3'>
                                                    <i className='fas fa-trash me-1 d-none d-sm-inline'></i>
                                                    <span className='d-none d-sm-inline'>Supprimer</span>
                                                    <span className='d-inline d-sm-none'>Suppr.</span>
                                                </Button>
                                            </Protected>}


                                    </div>

                                    {/* GROUP 2: Management/Delete (Right side of the button group, separated by a line) */}
                                    <div className="d-flex gap-2 border-start ps-2 ms-1">
                                        {intervention?.statut !== "planifie" && intervention?.statut !== "a_planifie" &&
                                            <Protected action='intervention:remettre_brouillon'>
                                                <Button
                                                    variant='warning'
                                                    onClick={() => handleSaveChanges({ statut: "planifie", valide: false })}
                                                    size='sm'
                                                    className=''>
                                                    <span className=''>Remettre brouillon</span>
                                                </Button>
                                            </Protected>
                                        }
                                        {intervention?.statut !== "termine" && intervention?.statut !== "annule" &&
                                            <Button
                                                variant='danger'
                                                onClick={async () => {
                                                    const raison = window.prompt("Raison d'annulation :");
                                                    if (raison === null) return; // user clicked Cancel
                                                    if (!raison.trim()) {
                                                        showNotification('Erreur', "Veuillez saisir une raison d'annulation", 'danger');
                                                        return;
                                                    }
                                                    const entryWithUser = {
                                                        type: 'note',
                                                        note: 'Intervention annulée : ' + raison,
                                                        createdBy: userData?.id
                                                    };
                                                    await onAddChronologieEntry(intervention._id, entryWithUser);
                                                    handleSaveChanges({ statut: "annule" });
                                                }}
                                                size='sm'
                                                className=''>
                                                <span>Annuler</span>
                                            </Button>
                                        }
                                        {intervention?.statut !== "termine" && intervention?.statut !== "annule" && intervention?.statut !== "a_planifie" &&
                                            <Button
                                                variant='success'
                                                onClick={() => handleSaveChanges({ statut: "termine" })}
                                                size='sm'
                                                className='me-5'>
                                                <span>Terminer</span>
                                            </Button>
                                        }
                                        {intervention?.statut == "termine" && !intervention?.valide && (
                                            <>
                                                {/* Custom Yes/No confirm for pose */}
                                                {showPoseConfirm && (
                                                    <div
                                                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                        style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                                        <div className="bg-white rounded-3 shadow-lg p-4" style={{ maxWidth: 360 }}>
                                                            <h5 className="fw-bold mb-2">Confirmation</h5>
                                                            <p className="text-muted mb-4">La pose a-t-elle été terminée avec succès ?</p>
                                                            <div className="d-flex gap-2 justify-content-end">

                                                                <Button
                                                                    variant="success"
                                                                    onClick={() => {
                                                                        setShowPoseConfirm(false);
                                                                        handleSaveChanges({ valide: true, rapport_intervention: { pose_faite: true } });
                                                                    }}>
                                                                    Oui
                                                                </Button>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => {
                                                                        setShowPoseConfirm(false);
                                                                        handleSaveChanges({ valide: true, rapport_intervention: { pose_faite: false } });
                                                                    }}>
                                                                    Non
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <Button
                                                    variant='info'
                                                    onClick={() => {
                                                        if (intervention?.type === "pose") {
                                                            setShowPoseConfirm(true);
                                                        } else {
                                                            handleSaveChanges({ valide: true });
                                                        }
                                                    }}
                                                    size='sm'
                                                    className='me-5'>
                                                    <span className=''>Valider</span>
                                                </Button>
                                            </>
                                        )}

                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className='p-0'>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className='px-4 pt-3 border-bottom-0'>
                        <Tab
                            eventKey='details'
                            title={
                                <span className='d-flex align-items-center'>
                                    <i className='fas fa-info-circle me-2'></i>Détails
                                </span>
                            }>
                            <DetailsTab {...detailsTabProps} />
                        </Tab>
                        {!isTechnician && (
                            <Tab
                                eventKey='chronologie'
                                title={
                                    <span className='d-flex align-items-center'>
                                        <i className='fas fa-history me-2'></i>Chronologie
                                    </span>
                                }>
                                <ChronologieTab {...chronologieTabProps} />
                            </Tab>
                        )}



                        <Tab
                            eventKey='devis'
                            title={
                                <span className='d-flex align-items-center'>
                                    <i className='fas fa-info-circle me-2'></i> Devis
                                </span>
                            }>
                            <DevisTab  {...devisTabProps} />
                        </Tab>
                        <Tab
                            eventKey='paiements'
                            title={
                                <span className='d-flex align-items-center'>
                                    <i className='fas fa-info-circle me-2'></i>Paiements
                                </span>
                            }>
                            <PaiementsTab {...paiementTabProps} />
                        </Tab>
                        <Tab
                            eventKey='rapports'
                            title={
                                <span className='d-flex align-items-center'>
                                    <i className='fas fa-file-alt me-2'></i>Rapports
                                </span>
                            }>
                            <RapportTab {...rapportTabProps} />
                        </Tab>
                        <Tab
                            eventKey="materials"
                            title={
                                <span className="d-flex align-items-center">
                                    <i className="fas fa-box me-2"></i>Matériels
                                </span>
                            }
                        >
                            <MaterialsTab {...materialsTabProps} />
                        </Tab>
                    </Tabs>
                </Modal.Body>

                <Modal.Footer className='bg-light border-top p-3'>
                    <div className='w-100 d-flex justify-content-end'>
                        <Button variant='outline-secondary' onClick={onHide} className='px-4'>
                            Fermer
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
            {openEmail && (
                <EmailSendPopup
                    isOpen={openEmail}
                    setIsOpen={setOpenEmail}
                    model="Intervention"
                    recordId={interventionId}
                    fnSuccess={handleEmailSent}
                    EmailType={emailType}
                />
            )}
        </>
    );
};

export default React.memo(InterventionDetailsModal);